import { useRefUpdated } from 'ui/hooks/useRefUpdated'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  type CountryCode,
  type NshiftCheckoutWidgetResult,
  type NshiftReceiver,
  type NshiftUpdateSessionRequestFE
} from 'ecosystem'
import { useForm, useWatch } from 'react-hook-form'
import { useDebouncedCallback, useDebounceValue } from 'ui/hooks'
import deepEqual from 'react-fast-compare'
import { overrideText } from 'ui/lib/overrides'
import { FetchError } from 'api/utils/next/fetchJson'
import { useOverridesContext } from 'ui/lib/overrides/hooks'
import { useRememberedAddress } from '../../hooks'
import { useNshiftGetDeliveryOptions, useNshiftUpdateSessionMutation } from './apiCallHooks'
import {
  type NshiftCheckoutEventDataChanged,
  type NshiftCheckoutEventDataChangedMeta,
  type NshiftCheckoutFormValues,
  type NshiftCheckoutWidgetInstance
} from './types'
import { type NshiftOverrides } from './translations'

interface UseNshiftCheckoutProps {
  storefrontOrderNumber: string
  cartTotalAmount: number
  initialData?: {
    isPickupFromStore?: boolean
    widgetResult?: NshiftCheckoutWidgetResult
    recipientAddress?: Partial<NshiftReceiver>
  }
  onDataChange?: (
    data: NshiftCheckoutEventDataChanged,
    meta: NshiftCheckoutEventDataChangedMeta
  ) => void
  /** Countries the carrier form offers. Used to reject a remembered address we cannot ship to. */
  availableCountries?: CountryCode[]
}
export const useNshiftCheckout = (props: UseNshiftCheckoutProps) => {
  const { onDataChange, cartTotalAmount, initialData, storefrontOrderNumber, availableCountries } =
    props
  const overrides = useOverridesContext<keyof NshiftOverrides>()

  const onDataChangeRef = useRefUpdated(onDataChange)

  const { remembered, remember, isOptedIn, setOptedIn } = useRememberedAddress('nshift')

  const widgetInstanceRef = useRef<NshiftCheckoutWidgetInstance>(undefined)

  const onReadyWidget = useCallback((widgetInstance: NshiftCheckoutWidgetInstance) => {
    widgetInstanceRef.current = widgetInstance
  }, [])

  const [selectedWidgetResult, setSelectedWidgetResult] =
    useState<NshiftCheckoutWidgetResult | null>(null)

  const selectedWidgetResultRef = useRef<NshiftCheckoutWidgetResult | null>(selectedWidgetResult)

  const form = useForm<NshiftCheckoutFormValues>({
    mode: 'onChange',
    defaultValues: {
      countryCode: initialData?.recipientAddress?.country ?? 'SE',
      postCode: initialData?.recipientAddress?.postalCode ?? '',
      city: initialData?.recipientAddress?.city ?? '',
      address: initialData?.recipientAddress?.address1 ?? '',
      isPickupFromStore: initialData?.isPickupFromStore ?? false
    }
  })

  // Read during render: `formState` is a Proxy that only subscribes to what it sees
  // accessed in the render pass, so reading `isDirty` solely inside an effect leaves it
  // permanently false.
  const isFormDirty = form.formState.isDirty

  // Seed the recipient form once something to seed from arrives — see the matching note
  // in `usePostNordCheckout`. Both sources land after `useForm` has taken its defaults
  // (the order because `orderResponse` is fetched, the device because the account has to
  // settle), so the order is allowed to overwrite a device seed if it arrives second.
  const seededFromRef = useRef<'none' | 'device' | 'order'>('none')
  useEffect(() => {
    if (isFormDirty || seededFromRef.current === 'order') return

    const orderRecipient = initialData?.recipientAddress

    if (orderRecipient?.postalCode) {
      seededFromRef.current = 'order'
      form.reset({
        ...form.getValues(),
        countryCode: orderRecipient.country || 'SE',
        postCode: orderRecipient.postalCode,
        city: orderRecipient.city ?? '',
        address: orderRecipient.address1 ?? ''
      })
      return
    }

    if (seededFromRef.current === 'device' || !remembered) return
    // The country came from the market the shopper picked in the checkout summary, or
    // from what the store ships to. A remembered address from elsewhere is not the one
    // being delivered to now.
    if (remembered.country !== form.getValues('countryCode')) return
    if (
      availableCountries?.length &&
      !availableCountries.includes(remembered.country as CountryCode)
    )
      return

    seededFromRef.current = 'device'
    form.reset({
      ...form.getValues(),
      postCode: remembered.postalCode ?? '',
      city: remembered.city ?? '',
      address: remembered.address1 ?? ''
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps -- dont put form
  }, [availableCountries, initialData, isFormDirty, remembered])

  const countryCode = useWatch({ control: form.control, name: 'countryCode' })
  const postCode = useWatch({ control: form.control, name: 'postCode' })
  const city = useWatch({ control: form.control, name: 'city' })
  const address = useWatch({ control: form.control, name: 'address' })
  const isPickupFromStore = useWatch({ control: form.control, name: 'isPickupFromStore' })

  const postCodeInvalid = form.getFieldState('postCode').invalid

  const [debouncedGetDeliveryOptionsParams] = useDebounceValue(
    {
      postCode,
      countryCode,
      city,
      address,
      postCodeInvalid
    },
    600,
    { equalityFn: deepEqual }
  )

  const {
    isLoading: isLoadingDeliveryOptions,
    data: deliveryOptionsData,
    error: errorDeliveryOptions
  } = useNshiftGetDeliveryOptions(
    {
      storefrontOrderNumber,
      cartTotalAmount,
      receiver: {
        country: debouncedGetDeliveryOptionsParams.countryCode,
        postalCode: debouncedGetDeliveryOptionsParams.postCode,
        city: debouncedGetDeliveryOptionsParams.city || undefined,
        address1: debouncedGetDeliveryOptionsParams.address || undefined
      }
    },
    {
      skip:
        debouncedGetDeliveryOptionsParams.postCodeInvalid ||
        !debouncedGetDeliveryOptionsParams.postCode ||
        !debouncedGetDeliveryOptionsParams.countryCode ||
        !debouncedGetDeliveryOptionsParams.city ||
        !debouncedGetDeliveryOptionsParams.address
    }
  )

  useEffect(() => {
    if (!widgetInstanceRef.current) {
      return
    }

    if (isLoadingDeliveryOptions) {
      widgetInstanceRef.current.disable()
    }
  }, [isLoadingDeliveryOptions])

  useEffect(() => {
    if (widgetInstanceRef.current && deliveryOptionsData) {
      widgetInstanceRef.current.updateOptions(deliveryOptionsData)
      widgetInstanceRef.current.enable()
    }
  }, [deliveryOptionsData])

  useEffect(() => {
    if (errorDeliveryOptions instanceof FetchError) {
      if (errorDeliveryOptions.data.error?.includes('INVALID_POSTAL_CODE')) {
        form.setError('postCode', {
          type: 'custom',
          message: overrideText('Ogiltigt postnummer', overrides?.errorInvalidPostalCode)
        })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- dont put form
  }, [errorDeliveryOptions])

  const isFormValid = form.formState.isValid

  const completeness =
    isPickupFromStore ||
    (!!selectedWidgetResult &&
      selectedWidgetResult.valid &&
      isFormValid &&
      !isLoadingDeliveryOptions &&
      !!widgetInstanceRef.current?.enabled)

  const totalPrice = useMemo(() => {
    if (isPickupFromStore) {
      return 0
    }

    return selectedWidgetResult?.totalPrice ?? 0
  }, [isPickupFromStore, selectedWidgetResult?.totalPrice])

  const { trigger: updateSessionMutation } = useNshiftUpdateSessionMutation()

  const updateSessionDebounce = useDebouncedCallback((payload: NshiftUpdateSessionRequestFE) => {
    void updateSessionMutation(payload).catch(() => void 0)
  }, 500)

  useEffect(() => {
    if (!completeness) {
      return
    }

    updateSessionDebounce({
      storefrontOrderNumber,
      isPickupFromStore,
      recipientAddress: {
        postalCode: postCode,
        country: countryCode,
        city,
        address1: address
      },
      widgetResult: selectedWidgetResult ?? undefined
    })
  }, [
    city,
    completeness,
    countryCode,
    isPickupFromStore,
    postCode,
    address,
    updateSessionDebounce,
    storefrontOrderNumber,
    selectedWidgetResult
  ])

  // Remember the values that actually produced the quote on screen, not the live field
  // contents — see the matching note in `usePostNordCheckout`. The form reaches "valid"
  // between keystrokes, so writing from the watched values stores half-typed addresses;
  // these debounced ones are what nShift was actually asked about.
  //
  // Pickup-from-store is not a delivery to this address, so there is nothing to remember.
  useEffect(() => {
    const { postCode: settledPostCode, countryCode: settledCountryCode } =
      debouncedGetDeliveryOptionsParams

    if (!completeness || isPickupFromStore || !settledPostCode) return

    remember({
      postalCode: settledPostCode,
      country: settledCountryCode,
      city: debouncedGetDeliveryOptionsParams.city,
      address1: debouncedGetDeliveryOptionsParams.address
    })
  }, [completeness, debouncedGetDeliveryOptionsParams, isPickupFromStore, remember])

  const dataRef = useRefUpdated<{
    data: NshiftCheckoutEventDataChanged
    meta: NshiftCheckoutEventDataChangedMeta
  }>({
    data: {
      isPickupFromStore,
      widgetResult: selectedWidgetResult ?? undefined,
      totalPrice,
      recipientAddress: {
        postalCode: postCode,
        country: countryCode,
        city,
        address1: address
      }
    },
    meta: {
      isValid: completeness,
      widgetResultChanged: false,
      totalPriceChanged: false,
      recipientAddressChanged: false,
      isPickupFromStoreChanged: false
    }
  })

  useEffect(() => {
    const { data, meta } = dataRef.current

    onDataChangeRef.current?.(data, {
      ...meta,
      isValid: completeness
    })
  }, [completeness, dataRef, onDataChangeRef])

  useEffect(() => {
    const { data, meta } = dataRef.current

    onDataChangeRef.current?.(
      {
        ...data,
        totalPrice
      },
      {
        ...meta,
        totalPriceChanged: true
      }
    )
  }, [dataRef, onDataChangeRef, totalPrice])

  useEffect(() => {
    const { data, meta } = dataRef.current

    onDataChangeRef.current?.(data, {
      ...meta,
      isPickupFromStoreChanged: true
    })
  }, [dataRef, onDataChangeRef, isPickupFromStore])

  useEffect(() => {
    const { data, meta } = dataRef.current

    onDataChangeRef.current?.(data, {
      ...meta,
      widgetResultChanged: true
    })
  }, [dataRef, onDataChangeRef, selectedWidgetResult])

  useEffect(() => {
    const { data, meta } = dataRef.current

    onDataChangeRef.current?.(
      {
        ...data,
        recipientAddress: {
          postalCode: postCode,
          country: countryCode,
          city,
          address1: address
        }
      },
      {
        ...meta,
        recipientAddressChanged: true
      }
    )
  }, [dataRef, onDataChangeRef, postCode, countryCode, city, address])

  const onChangeWidgetResult = useCallback((widgetResult: NshiftCheckoutWidgetResult) => {
    setSelectedWidgetResult(widgetResult)
    selectedWidgetResultRef.current = widgetResult
  }, [])

  return {
    form,
    isPickupFromStore,
    onChangeWidgetResult,
    onReadyWidget,
    deliveryOptionsData,
    isLoadingDeliveryOptions,
    isRememberOptedIn: isOptedIn,
    setRememberOptedIn: setOptedIn
  }
}
